import React, { useEffect, useState } from 'react'
import Carousel from './carousel/Carousel'
import ListProducts from './listProducts';
import { inject, observer } from "mobx-react";
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import SmallAlert from '../modals/smallAlert';
import VoucherModal from '../modals/voucherModal';
import LoyaltyModal from '../modals/loyaltyModal';
import { DateTime } from "luxon";
import { OrderKind } from "../utils/enums";
import { campaignTargetsCustomerClient } from "../utils/campaignTargeting";
import {pickLanguageWord} from '../utils/pickLanguageWord.js';
import GreenSubmitButton from "../buttons/greenSubmitButton";
import Colors from '../assets/colors';
import {DEFAULT_EMAIL} from "../assets/globalConstants";

// Translations
import _useTranslate from '../hooks/_useTranslate';
import {commonEL} from "../locales/el/common";

//Which voucher has already been ANNOUNCED this JS session, keyed by identity + campaign.
//Module scope on purpose: component state cannot dedupe this. Logging in briefly leaves a second
//instance of this screen mounted, and each instance runs its own effect and opens its own modal -
//two identical dialogs stacked. A module-level marker is shared by every instance, so the offer is
//announced once per identity per session however many times the screen mounts. Logging in changes
//the key, so a newly eligible customer still gets told about their offer.
const announcedVouchers = new Set();

const HomeBody = ({ feathersStore })=> {

  let common = _useTranslate(feathersStore.language, "common");

  let navigate = useNavigate();

  const {name} = useParams(); 
  let [searchParams, setSearchParams] = useSearchParams();

  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [voucherModal, setVoucherModal] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const [loyaltyModal, setLoyaltyModal] = useState(false);
  const [loyaltyReward, setLoyaltyReward] = useState(0);
    
  let catId;
  let title;
  let titleTranslated; 

  //Browser BackButton and Refresh usage
  useEffect(()=>{    
    let payload =  {searchText: "", title: commonEL.all, titleTranslated:  pickLanguageWord(feathersStore.language, "all")};
    if ((name || searchParams) && feathersStore.categories) {    
      searchTree(feathersStore.categories, name);
     name && Object.assign(payload, {categoryId: catId, title: title, titleTranslated: titleTranslated});
     searchParams && Object.assign(payload, {searchText: searchParams.get('search') || ""});            
    }
    feathersStore.setSearchCategory(payload); 
 },[name, searchParams,  feathersStore.categories, feathersStore.language]);

  useEffect(()=>{
    const table = searchParams.get('__table');
    if(table && feathersStore.settings?.TABLE_QRCODE_PSW){
      if(table.slice(0, 8) === feathersStore.settings?.TABLE_QRCODE_PSW){
        const resolved = table?.slice(9);
        // Idempotent on re-runs: this effect now re-fires whenever settings/auth land (see the
        // dep note below), so only announce (and re-register) a session that actually CHANGED —
        // otherwise every settings refetch would re-open the success modal mid-visit.
        if(resolved !== feathersStore.tableId){
          feathersStore.setTableId(resolved)
          setWithExpiry('tableId', resolved, 6 * 60 * 60 * 1000)  //6h
          setSuccessModal(true)
        }
      }else if(getWithExpiry('tableId')){
        feathersStore.setTableId(getWithExpiry('tableId'))
      }else {
        setErrorModal(true)
      }
    }
  // TWO deps, not `a && b`: the boolean-AND collapsed to `false` for an unauthenticated guest, so
  // this effect ran exactly once at mount — usually before settings arrived, when TABLE_QRCODE_PSW
  // was still undefined — and the QR scan silently registered nothing. On a TABLE_QR_ORDERING_ONLY
  // tenant that leaves the whole catalog visible but every add-to-cart disabled with no error
  // (kipi 2026-08-06). Same defect class as the Checkout plant effect fixed in b028a9a.
  },[feathersStore.isAuthenticated, feathersStore.settings]);

  useEffect(()=>{ //voucher
    //The effect now re-runs on identity change, so a login leaves the GUEST run still in
    //flight behind it - both awaited coupons() + eligibility, and both then opened a modal,
    //stacking two. Cancel the superseded run instead of letting it finish.
    let voucherRunCancelled = false;
    const asyncFn = async() => {
      const now = DateTime.now().setZone(feathersStore.timezone());
      const _vouchers = await feathersStore.coupons();
      //Only advertise what this visitor can actually redeem. The banner used to filter on `general`
      //and `display` alone, so it pushed a first-order code at a guest who is then refused at the
      //cart — and would advertise a TILL-ONLY campaign inside the customer app. Same targeting the
      //cart applies, so the promise on the home screen matches what the code will do.
      const kinds = feathersStore.isTableOrder() ? [OrderKind.webTable] : [OrderKind.webDelivery, OrderKind.webTakeAway];
      const dateValid = (v) => v?.endDate
        ? DateTime.fromISO(v.endDate) > now
        : DateTime.fromMillis(v.createdAt).plus({days : v.duration}) > now;
      const shown = (_vouchers || []).filter(coupon =>
        coupon?.general && coupon?.display && dateValid(coupon)
        && campaignTargetsCustomerClient(coupon, kinds));
      //Only pay for the eligibility round-trip when a first-order campaign is actually on offer.
      //For a guest it costs nothing anyway - isFirstOrderEligible returns false on the email alone.
      const firstOrderEligible = shown.some(v => v.firstOrderOnly)
        ? await feathersStore.isFirstOrderEligible()
        : false;
      const offerable = shown.filter(v => !v.firstOrderOnly || firstOrderEligible);
      //Prefer the once-only offer: it disappears the moment they order, so it is the one worth
      //interrupting someone for. A standing voucher will still be there tomorrow.
      const voucher = offerable.find(v => v.firstOrderOnly) || offerable[0];
      //Key on identity + campaign so a login announces the new offer exactly once.
      const announceKey = (feathersStore?.user?._id || 'guest') + ':' + voucher?._id;
      if(voucher && !voucherRunCancelled && !announcedVouchers.has(announceKey)){
        announcedVouchers.add(announceKey);             
            setVoucher(voucher);
            setVoucherModal(true);
      }
    }
    feathersStore?.isAuthenticated && asyncFn();
    return () => { voucherRunCancelled = true; };
  //Re-run on IDENTITY change, not just on isAuthenticated: that flag is already true for the
  //shared guest and stays true through a login, so the effect never fired again and the banner
  //kept showing the guest's answer until the app was restarted.
  },[feathersStore?.isAuthenticated, feathersStore?.user?._id]);

  useEffect(()=>{ //loyalty
    if(feathersStore?.user?.email !== DEFAULT_EMAIL){
      if(feathersStore.user?.loyaltyRewards?.length > 0){
        const totalReward = feathersStore.user?.loyaltyRewards
          ?.filter(r => !r?.applied)//Old rewards not yet applied
          ?.map(reward => reward?.value || 0)
          ?.reduce((a, b) => +a + +b, 0);
        if(totalReward > 0){
          setLoyaltyReward(totalReward);
          setLoyaltyModal(true);
        }
      }
    }
  },[feathersStore?.user]);

  const setWithExpiry = (key, value, ttl) => {
    const now = new Date()
    const item = {
      value: value,
      expiry: now.getTime() + ttl, //ttl in milliseconds
    }
    localStorage.setItem(key, JSON.stringify(item))
  }

  const emptyCart = () => {  
    localStorage.setItem("fdwa_cart", JSON.stringify([]));
    feathersStore.resetOrderItem();
    feathersStore.setCartLength(0);
  }

  const getWithExpiry = (key) => {
    const itemStr = localStorage.getItem(key)   
    if (!itemStr) {
      return 'eshop'
    }
    const item = JSON.parse(itemStr)
    const now = new Date()
    if (now.getTime() > item.expiry) { 
      localStorage.removeItem(key);
      emptyCart();
      return 'eshop'
    }
    return item.value
  }

 const searchTree = (tree, name) => {
   tree.forEach(element => {
    if(element.name?.trim() === name || feathersStore._translateCategoryName(element)?.trim === name){    
      catId = element.id;
      title =  element.name?.trim();
      titleTranslated = feathersStore._translateCategoryName(element);
    }else{
      element.childrens && searchTree(element.childrens, name)
    }
   }) 
  }  

  const closeErrorModal = () => {
    setErrorModal(false);  
    let errorWindow = window.open("https://appdate.gr", "_self");  
  }

  const closeVoucherModal = () => {
    setVoucherModal(false);  
  }

  const closeLoyaltyModal = () => {
    setLoyaltyModal(false);  
  }

  return(
    <>    
      <div className="body-container">
        {/* hideWebAppCarousel is the tenant's own switch (Settings -> web section). It is a
            HIDE flag on purpose: absent/false leaves the carousel showing, so the tenants who
            have never seen this toggle are unaffected. Gating the WRAPPER too, so hiding it
            leaves no empty band above the categories. */}
        {feathersStore?.isAuthenticated && (!name) && feathersStore?.settings
          && !feathersStore?.settings?.hideWebAppCarousel ? (
          <>
            <div className="column-flex-center-100">
              <Carousel/>
            </div>
          </>)
          : null
         }
        {/* showBooking is the master switch (absent = on); appBooking stays the owner's
            customer-facing switch, so a shop that runs the staff Bookings module with diner
            self-booking off keeps exactly that. */}
        {feathersStore?.settings?.showBooking !== false
          && feathersStore?.settings?.appBooking && !feathersStore.tableQrOrderingOnly() && !feathersStore.qrReadOnly()
          && !feathersStore.preOrderBooking && /*hide while ordering for a booking*/
          <div className="reservation-button-container">
            <GreenSubmitButton
              onClick={() => navigate("/reservation")}
              width="calc(100px + 14vw)"
              height="calc(14px + 2vw)"
              title={common.reserveTable}
              color={undefined}
              marginBottom={0}
              marginLeft={0}
              marginTop={"calc(8px + 2vw)"}
              fontSize={"calc(9px + 0.7vw)"}
              textTransform={"none"}
            ></GreenSubmitButton>
          </div>
        }
        <ListProducts />
            
     </div>    
     <SmallAlert 
        show={successModal} 
        mode="qrcode"
        heading={common.scannerSuccess} 
        onClose={() => setSuccessModal(false)} 
        message={common.scannerMessage} 
      />   
      <SmallAlert 
        show={errorModal}    
        heading={common.scannerErrorHeader} 
        mode="error"
        onClose={() => closeErrorModal()} 
        message={common.scannerError} 
      /> 
      <LoyaltyModal 
        show={loyaltyModal}      
        reward={loyaltyReward}
        onClose={() => closeLoyaltyModal()} 
      />
      {voucher &&  <VoucherModal 
        show={voucherModal}    
        code={voucher.code}     
        type={voucher.type} 
        reward={voucher.reward}
        minimumOrder={voucher?.minimumOrder || 0}
        promotionText={voucher.promotionText}
        onClose={() => closeVoucherModal()} 
      /> }
    </>
  )
}

export default inject('feathersStore')(observer(HomeBody));